import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { type ComponentProps, type ReactNode, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { ATTENTION_STATUS_ACTIONS } from 'partner360/entities/attentionStatuses'
import { useAcknowledgmentPendingSigners } from 'partner360/features/acknowledgmentStatusDialog'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { invalidateFinancialCockpitData } from '../../../../model/financialCockpitSlice'
import { selectEffectiveCockpitFYear } from '../../../../model/selectors'

import { AttentionNextStepRow } from './AttentionNextStepRow'
import { useCockpitPlanSignerRows } from './useCockpitPlanSignerRows'
import { usePlanSignFlow } from './usePlanSignFlow'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.nextSteps

type TRowProps = Omit<ComponentProps<typeof AttentionNextStepRow>, 'onClick' | 'tone' | 'labelKey'>

interface IManagerOwnSignStepProps {
  row: TRowProps
  partnerId: string
  /** The offering whose plan is signed. */
  offering: string
  /** Opens the partner's plan in a new tab beside the signature; the PBM has none. */
  onOpenPlan?: () => void
  /** The step as it stands when the plan waits for no signature of the reader. */
  children: ReactNode
}

/**
 * A plan the server still counts as not completed only because no PBM has signed it yet: the
 * reader whose signature the plan waits for signs it right here, as on the partner's cockpit.
 * Every other reader keeps the step's own act.
 */
export const ManagerOwnSignStep = ({
  row,
  partnerId,
  offering,
  onOpenPlan,
  children,
}: IManagerOwnSignStepProps) => {
  const dispatch = useAppDispatch()
  const fYear = useSelector(selectEffectiveCockpitFYear)

  const handleUpdated = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])

  const knownItems = useCockpitPlanSignerRows(partnerId, offering)
  const { hasLoaded, ownRowToSign } = useAcknowledgmentPendingSigners({
    partnerId,
    fYear,
    offering,
    knownItems,
    onUpdated: handleUpdated,
  })

  const signFlow = usePlanSignFlow({ partnerId, fYear, offering })

  /* While the signers are read the button waits, so the step does not first offer one act and
     then another. The flow stays mounted whatever the row offers, so a signature under way is not
     cut off. */
  const step = !hasLoaded ? (
    <AttentionNextStepRow
      {...row}
      tone="go"
      labelKey={ATTENTION_STATUS_ACTIONS.acknowledge.labelKey}
      shortLabelKey={KEYS.short.sign}
      icon="kbase kbase-check"
      loading
      disabled
      onClick={() => undefined}
    />
  ) : ownRowToSign ? (
    <AttentionNextStepRow
      {...row}
      tone="sign"
      labelKey={ATTENTION_STATUS_ACTIONS.acknowledge.labelKey}
      shortLabelKey={KEYS.short.sign}
      icon="kbase kbase-check"
      loading={signFlow.isSigning}
      extraAction={
        onOpenPlan && {
          icon: 'kbase kbase-external-link',
          labelKey: KEYS.openPlan,
          shortLabelKey: KEYS.short.plan,
          onClick: onOpenPlan,
        }
      }
      onClick={() => signFlow.start(ownRowToSign.EntryId)}
    />
  ) : (
    children
  )

  return (
    <>
      {step}
      {signFlow.element}
    </>
  )
}

ManagerOwnSignStep.displayName = 'ManagerOwnSignStep'
